(() => {
  'use strict';

  const VERSION = '2.0.3';
  const offerStates = new Map();
  const sectionFlights = new Map();
  const cartCoordinator = window.IzzatCartMutations;
  const debounce = (callback, wait = 0) => {
    let timeoutId;
    return function debounced(...args) {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback.apply(this, args), wait);
    };
  };
  if (typeof window.debounce !== 'function') window.debounce = debounce;

  const pauseAllMedia = () => {
    const pause = (nodes, action) => {
      for (const node of nodes || []) {
        try { action(node); } catch { /* one hostile media surface must never break the caller */ }
      }
    };
    pause(document.querySelectorAll?.('.js-youtube'), (frame) => {
      frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    pause(document.querySelectorAll?.('.js-vimeo'), (frame) => {
      frame.contentWindow?.postMessage('{"method":"pause"}', '*');
    });
    pause(document.querySelectorAll?.('video'), (video) => video.pause?.());
    pause(document.querySelectorAll?.('product-model'), (model) => model.modelViewerUI?.pause?.());
  };
  if (typeof window.pauseAllMedia !== 'function') window.pauseAllMedia = pauseAllMedia;

  const quantityValue = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
  };

  const shopifyRoute = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${String(path).replace(/^\//, '')}`.replace(/\/+/g, '/');
  };

  const route = (name, path) => window.routes?.[name] || shopifyRoute(path);

  const cartError = () => window.cartStrings?.error || 'Unable to update cart. Please try again.';
  const cartUnknownMessage = () => window.cartStrings?.unknownResult || cartError();
  const createCartUnknownError = (reason = 'UNKNOWN_RESULT') => {
    const error = new Error(cartUnknownMessage());
    error.code = 'UNKNOWN_RESULT';
    error.reason = reason;
    return error;
  };
  const isCartUnknownError = (error) => error?.code === 'UNKNOWN_RESULT';
  const createCartRejectedError = (message = cartError()) => {
    const error = new Error(message || cartError());
    error.code = 'CART_REJECTED';
    error.status = 422;
    return error;
  };
  const isCartRejectedError = (error) => error?.code === 'CART_REJECTED' && error?.status === 422;
  const cartMutationLocked = () => Boolean(window.__izzatCartMutationLock);
  const lockCartMutation = (detail = {}) => {
    if (typeof cartCoordinator?.lockCartMutation === 'function') return cartCoordinator.lockCartMutation(detail);
    const lock = Object.freeze({ outcome: 'UNKNOWN_RESULT', ...detail });
    window.__izzatCartMutationLock = lock;
    window.__izzatCartDiagnostics = lock;
    document.dispatchEvent(new CustomEvent('izzat-theme-v2:cart-unknown', { detail: lock }));
    return lock;
  };

  const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

  const normalizeLine = (item) => {
    if (!isRecord(item)) return null;
    const key = String(item.key || '').trim();
    const variantId = String(item.variant_id ?? item.id ?? '').trim();
    const itemQuantity = Number.parseInt(item.quantity, 10);
    if (!key || !variantId || !Number.isSafeInteger(itemQuantity) || itemQuantity < 1) return null;
    return { key, variantId, quantity: itemQuantity };
  };

  const cartLines = (payload) => {
    if (!isRecord(payload)) return [];
    const items = Array.isArray(payload.items) ? payload.items : [payload];
    const lines = items.map(normalizeLine);
    return lines.every(Boolean) ? lines : [];
  };

  const responseCovers = (lines, requests) => {
    if (!lines.length || !requests.length) return false;
    const quantities = new Map();
    lines.forEach(({ variantId, quantity }) => quantities.set(variantId, (quantities.get(variantId) || 0) + quantity));
    return requests.every(({ variantId, quantity }) => (quantities.get(variantId) || 0) >= quantity);
  };

  const cartMatches = (cart, expectedLines) => {
    if (!isRecord(cart) || !Array.isArray(cart.items) || !expectedLines.length) return false;
    const cartLines = new Map();
    for (const item of cart.items) {
      const line = normalizeLine(item);
      if (line) cartLines.set(line.key, line);
    }
    // Shopify agrupa linhas da mesma variante: somamos o esperado por chave
    // e conferimos cobertura (>=) em vez de igualdade exata.
    const expectedByKey = new Map();
    for (const expected of expectedLines) {
      const current = expectedByKey.get(expected.key);
      if (current) {
        if (current.variantId !== expected.variantId) return false;
        current.quantity += expected.quantity;
      } else {
        expectedByKey.set(expected.key, { quantity: expected.quantity, variantId: expected.variantId });
      }
    }
    for (const [key, expected] of expectedByKey) {
      const actual = cartLines.get(key);
      if (!actual || actual.variantId !== expected.variantId || actual.quantity < expected.quantity) return false;
    }
    return true;
  };

  const reconcileCart = async (expectedLines) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(route('cart_url', 'cart') + '.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) continue;
        const cart = await response.json();
        if (cartMatches(cart, expectedLines)) return cart;
      } catch (_error) {
      }
    }
    return null;
  };

  const offerState = (sectionId) => offerStates.get(String(sectionId || '')) || {
    sectionId: String(sectionId || ''),
    quantity: 1,
    variantId: '',
    formattedTotal: '',
  };

  const syncOfferPriceTargets = (state) => {
    const total = String(state?.formattedTotal || '').trim();
    if (!total || !state?.sectionId) return;
    const targets = document.querySelectorAll?.(`.main-atc-price-span-${escapeSelector(state.sectionId)}`);
    for (const target of targets || []) {
      if (target.textContent !== total) target.textContent = total;
    }
  };

  const updateOfferState = (sectionId, patch = {}) => {
    const key = String(sectionId || '');
    const next = Object.freeze({ ...offerState(key), ...patch, sectionId: key });
    offerStates.set(key, next);
    syncOfferPriceTargets(next);
    document.dispatchEvent(new CustomEvent('izzat:offer-change', { detail: next }));
    return next;
  };

  const runSingleFlight = (owner, key, operation) => {
    if (owner[key]) return owner[key];
    owner[key] = Promise.resolve().then(operation).finally(() => { owner[key] = null; });
    return owner[key];
  };

  const runSectionFlight = (sectionId, operation, fallbackOwner) => {
    const key = String(sectionId || '');
    if (!key) return runSingleFlight(fallbackOwner || operation, '_sectionFlight', operation);
    const active = sectionFlights.get(key);
    if (active) return active;
    const flight = Promise.resolve().then(operation).finally(() => sectionFlights.delete(key));
    sectionFlights.set(key, flight);
    return flight;
  };

  const cartMutationBusy = () => typeof cartCoordinator?.cartMutationBusy === 'function'
    ? cartCoordinator.cartMutationBusy()
    : sectionFlights.has('_cart');
  const cartMutationPending = (intentKey) => typeof cartCoordinator?.cartMutationPending === 'function'
    && cartCoordinator.cartMutationPending(intentKey);
  const runCartMutation = (source, operation, options = {}) => typeof cartCoordinator?.run === 'function'
    ? cartCoordinator.run(source, operation, options)
    : runSectionFlight('_cart', operation);
  const cartCheckoutBlocked = () => typeof cartCoordinator?.checkoutBlocked === 'function'
    ? cartCoordinator.checkoutBlocked()
    : cartMutationLocked() || cartMutationBusy();
  const guardCartCheckoutClick = (event) => typeof cartCoordinator?.guardClick === 'function'
    ? cartCoordinator.guardClick(event)
    : !cartCheckoutBlocked();
  const runCartFlight = (sectionId, operation) => {
    const key = String(sectionId || '');
    return runSectionFlight(key, () => runCartMutation('product-form', operation, {
      intentKey: `product:${key}`,
      joinActive: true,
    }));
  };
  const sectionBusy = (sectionId) => sectionFlights.has(String(sectionId || ''));

  const closeQuickAddForCart = (quickAddModal) => new Promise((resolve, reject) => {
    const eventTarget = document.body;
    if (!eventTarget?.addEventListener || typeof quickAddModal?.hide !== 'function') {
      reject(createCartUnknownError('QUICK_ADD_HANDOFF'));
      return;
    }

    const handleModalClosed = () => resolve();
    eventTarget.addEventListener('modalClosed', handleModalClosed, { once: true });
    try {
      quickAddModal.hide(true);
    } catch (_error) {
      eventTarget.removeEventListener?.('modalClosed', handleModalClosed);
      reject(createCartUnknownError('QUICK_ADD_HANDOFF'));
    }
  });

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const moneyPlaceholderFormats = Object.freeze({
    amount: [null, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [null, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [null, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [null, ' ', ','],
    amount_with_period_and_space_separator: [null, ' ', '.'],
  });

  const threeDecimalCurrencies = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);
  const zeroDecimalCurrencies = new Set([
    'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
  ]);
  const twoDecimalCurrencies = new Set((
    'AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BMD BND BOB BRL BSD BTN BWP BYN BZD '
    + 'CAD CDF CHF CNY COP CRC CUP CVE CZK DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD '
    + 'GTQ GYD HKD HNL HTG HUF IDR ILS INR IRR JMD KES KGS KHR KPW KYD KZT LAK LBP LKR LRD LSL MAD '
    + 'MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD PAB PEN PGK PHP '
    + 'PKR PLN QAR RON RSD RUB SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP STN SVC SYP SZL THB TJS '
    + 'TMT TOP TRY TTD TWD TZS UAH USD UYU UZS VED VES WST XCD XCG YER ZAR ZMW ZWG ZWL'
  ).split(' '));

  const currencyMetadata = (currencyCode) => {
    const code = String(currencyCode || '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return null;
    if (threeDecimalCurrencies.has(code)) return Object.freeze({ code, precision: 3, scale: 1000 });
    if (zeroDecimalCurrencies.has(code)) return Object.freeze({ code, precision: 0, scale: 100 });
    if (twoDecimalCurrencies.has(code)) return Object.freeze({ code, precision: 2, scale: 100 });
    return null;
  };

  const currencySubunitScale = (currencyCode) => currencyMetadata(currencyCode)?.scale ?? null;

  const normalizeMoneySubunits = (subunits) => {
    const value = typeof subunits === 'string' ? subunits.trim() : subunits;
    const parsed = Number(value);
    return value !== '' && Number.isFinite(parsed) ? parsed : null;
  };

  const majorToSubunits = (majorUnits, currencyCode) => {
    const metadata = currencyMetadata(currencyCode);
    const amount = normalizeMoneySubunits(majorUnits);
    if (!metadata || amount == null || amount < 0) return null;
    return Math.round(amount * metadata.scale);
  };

  const formatMoneyFallback = (subunits, format, metadata) => {
    const rendered = String(format || '{{ amount }}').replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (placeholder, name) => {
        const options = moneyPlaceholderFormats[name];
        if (!options) return placeholder;
        const [configuredPrecision, thousands, decimal] = options;
        const precision = configuredPrecision ?? metadata.precision;
        const [whole, fraction] = (subunits / metadata.scale).toFixed(precision).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      },
    );
    return /\{\{/.test(rendered) ? '' : rendered;
  };

  const formatPresentmentMoney = (subunits, metadata, locale) => {
    if (typeof Intl?.NumberFormat !== 'function') return '';
    try {
      return new Intl.NumberFormat(locale || document.documentElement?.lang || undefined, {
        style: 'currency',
        currency: metadata.code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: metadata.precision,
        maximumFractionDigits: metadata.precision,
      }).format(subunits / metadata.scale);
    } catch (_error) {
      return '';
    }
  };

  const formatMoney = (subunits, format, currencyCode, locale, formatCurrencyCode) => {
    const metadata = currencyMetadata(currencyCode);
    const safeSubunits = normalizeMoneySubunits(subunits);
    if (!metadata || safeSubunits == null) return '';
    const normalizedFormatCurrency = String(formatCurrencyCode || '').trim().toUpperCase();
    if (normalizedFormatCurrency !== metadata.code) {
      return formatPresentmentMoney(safeSubunits, metadata, locale);
    }
    return formatMoneyFallback(safeSubunits, format, metadata);
  };

  const sectionProductForm = (sectionId) => {
    if (!sectionId) return null;
    const selector = `product-form[data-section="${escapeSelector(sectionId)}"]`;
    const matches = [...document.querySelectorAll(selector)];
    const main = matches.filter((form) => form.dataset?.main === 'true');
    return main.length === 1 ? main[0] : matches.length === 1 ? matches[0] : null;
  };

  class IzzatProductForm extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.form = this.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
      if (!this.form) return;
      this._onSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
      if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit);
      this._initialized = false;
    }

    handleSubmit(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (!this.form) return Promise.resolve(false);

      const trigger = event?.submitter || (event?.currentTarget !== this.form ? event?.currentTarget : null);
      return runCartFlight(this.dataset.section, () => this.submit(trigger));
    }

    async submit(trigger) {
      const submitButton = this.form.querySelector('[type="submit"][name="add"], .product-form__submit');
      if (cartMutationLocked()) {
        this.showError(cartUnknownMessage());
        this.setBusy(false, submitButton, true);
        return false;
      }
      if (submitButton && submitButton.disabled) return false;

      this.setBusy(true, submitButton);
      this.clearError();
      let lockControls = false;

      try {
        const formData = new FormData(this.form);
        this.applyPerUnitVariants(formData);
        this.applyAdditionalItems(formData);
        const requests = this.requestedItems(formData);
        if (!requests.length) throw new Error(window.variantStrings?.unavailable || cartError());
        const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const quickAddModal = this.closest?.('quick-add-modal') || null;
        if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
          const sections = cartUi.getSectionsToRender().map((section) => section.section || section.id);
          formData.set('sections', [...new Set(sections)].join(','));
          formData.set('sections_url', window.location.pathname);
        }

        let response;
        try {
          response = await fetch(route('cart_add_url', 'cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
        } catch (_error) {
          throw createCartUnknownError('POST_TRANSPORT');
        }
        const result = await this.readAddResponse(response, requests);
        const lines = cartLines(result);

        const cart = await this.readCart(lines);
        if (!cart) throw createCartUnknownError('CART_READBACK');
        if (cartUi) {
          if (typeof cartUi.renderContents !== 'function' || !result.sections) {
            throw createCartUnknownError('CART_UI_RENDER');
          }
          if (quickAddModal && typeof cartUi.open !== 'function') {
            throw createCartUnknownError('CART_UI_RENDER');
          }
          if (!quickAddModal) cartUi.setActiveElement?.(trigger || submitButton);

          let rendered = false;
          try {
            rendered = cartUi.renderContents(result, Boolean(quickAddModal), requests[0]?.variantId);
          } catch (_error) {
            rendered = false;
          }
          if (rendered !== true) throw createCartUnknownError('CART_UI_RENDER');
          if (quickAddModal) {
            await closeQuickAddForCart(quickAddModal);
            cartUi.open();
          }
        }

        const detail = {
          source: 'izzat-theme-v2',
          sectionId: this.dataset.section || '',
          requestedQuantity: requests.reduce((total, item) => total + item.quantity, 0),
          requestedItems: requests,
          variantId: requests[0].variantId,
          cart,
          response: result,
        };
        window.__izzatCartDiagnostics = detail;
        document.dispatchEvent(new CustomEvent('izzat-theme-v2:cart-confirmed', { detail }));
        document.dispatchEvent(new CustomEvent('izzat:cart-updated', { detail }));

        if (this.querySelector('.product-variant-id[data-skip-cart="true"]')) {
          window.location.assign(route('checkout_url', 'checkout'));
        } else if (!cartUi && window.routes?.cart_url) {
          window.location.assign(window.routes.cart_url);
        }
        return true;
      } catch (error) {
        if (isCartUnknownError(error)) {
          lockControls = true;
          lockCartMutation({
            reason: error.reason,
            sectionId: this.dataset.section || '',
            source: 'product-form',
          });
        }
        this.showError(error instanceof Error ? error.message : String(error));
        document.dispatchEvent(new CustomEvent('izzat-theme-v2:cart-error', { detail: { error } }));
        return false;
      } finally {
        this.setBusy(false, submitButton, lockControls);
      }
    }

    async readAddResponse(response, requests) {
      let result;
      try {
        result = await response.json();
      } catch (_error) {
        throw response.ok ? createCartUnknownError('ADD_RESPONSE_PARSE') : new Error(cartError());
      }

      if (!response.ok || (isRecord(result) && result.status)) {
        const status = Number(response.status || result?.status);
        if (status !== 422) throw createCartUnknownError('ADD_RESPONSE_HTTP');
        throw new Error(result?.description || result?.message || cartError());
      }
      if (!isRecord(result)) throw createCartUnknownError('ADD_RESPONSE_SHAPE');

      const lines = cartLines(result);
      if (!responseCovers(lines, requests)) throw createCartUnknownError('ADD_RESPONSE_MISMATCH');
      return result;
    }

    async readCart(expectedLines) {
      return reconcileCart(expectedLines);
    }

    applyPerUnitVariants(formData) {
      const sectionId = escapeSelector(this.dataset.section || '');
      const breaks = document.querySelector(`quantity-breaks[data-section="${sectionId}"]`);
      const selected = breaks?.querySelector('input[name="quantity"]:checked');
      const label = selected?.id ? breaks.querySelector(`label[for="${escapeSelector(selected.id)}"]`) : null;
      const selectors = [...(label?.querySelectorAll('.quantity-break__selector-item') || [])];
      if (!selectors.length) return;

      let variants = [];
      try {
        variants = JSON.parse(breaks.querySelector(':scope > script[type="application/json"]')?.textContent || '[]');
      } catch (_error) {
        throw new Error(window.variantStrings?.unavailable || 'Selected variant is unavailable.');
      }
      if (!Array.isArray(variants) || !variants.length) {
        throw new Error(window.variantStrings?.unavailable || 'Selected variant is unavailable.');
      }
      const ids = selectors.map((selector) => {
        const options = [...selector.querySelectorAll('select')].map((select) => select.value);
        return variants.find((variant) => (
          variant?.available
          && Array.isArray(variant.options)
          && variant.options.every((option, index) => option === options[index])
        ))?.id;
      });
      if (ids.some((id) => !id)) throw new Error(window.variantStrings?.unavailable || 'Selected variant is unavailable.');

      const itemSpecificFields = [...formData.entries()].filter(([key]) => (
        key === 'selling_plan'
        || key === 'parent_id'
        || /^properties\[.+]$/.test(key)
      ));
      formData.delete('id');
      formData.delete('quantity');
      itemSpecificFields.forEach(([key]) => formData.delete(key));
      ids.forEach((id, index) => {
        formData.append(`items[${index}][id]`, String(id));
        formData.append(`items[${index}][quantity]`, '1');
        itemSpecificFields.forEach(([key, value]) => {
          const itemKey = key.startsWith('properties[')
            ? `items[${index}][properties]${key.slice('properties'.length)}`
            : `items[${index}][${key}]`;
          formData.append(itemKey, value);
        });
      });
    }

    productUpsells() {
      const data = this.dataset;
      const main = data.main === 'true';
      const section = data.productSection || data.section;
      if ((!main && data.includePdpUpsells !== 'true') || !section) return [];
      const result = [];
      for (const item of document.querySelectorAll(`product-info-upsell[data-product-section="${escapeSelector(section)}"][data-collect-with-main="true"]`)) {
        const itemData = item.dataset;
        if (itemData.productSection !== section || itemData.collectWithMain !== 'true'
          || itemData.selected !== 'true' || item.closest?.('cart-drawer')
          || (!main && itemData.upsellSource !== 'standalone')) continue;
        const id = itemData.id?.trim();
        if (!id || item.dataset.variantAvailable !== 'true') {
          throw new Error(window.variantStrings?.unavailable || cartError());
        }
        result.push({ id, quantity: quantityValue(itemData.quantity) });
      }
      return result;
    }

    applyAdditionalItems(formData) {
      const sectionId = escapeSelector(this.dataset.section || '');
      const breaks = document.querySelector(`quantity-breaks[data-section="${sectionId}"]`);
      const selected = breaks?.querySelector('input[name="quantity"]:checked');
      const activeOffer = selected?.id ? breaks.querySelector(`label[for="${escapeSelector(selected.id)}"]`) : null;
      const extrasRoot = activeOffer || (!breaks ? document : null);
      const extras = [...(extrasRoot?.querySelectorAll(`quantity-break-upsells[data-section="${sectionId}"] .quantity-break__upsell[data-selected="true"][data-product]`) || [])]
        .map(({ dataset }) => ({ id: String(dataset.product || '').trim(), quantity: quantityValue(dataset.quantity) }));
      extras.push(...this.productUpsells());
      if (!extras.length) return;

      let nextIndex = [...formData.keys()].reduce((highest, key) => {
        const match = key.match(/^items\[(\d+)]/);
        return match ? Math.max(highest, Number(match[1]) + 1) : highest;
      }, 0);
      const seenVariantIds = new Set(
        [...formData.entries()]
          .filter(([key]) => /^items\[\d+]\[id]$/.test(key))
          .map(([, value]) => String(value || '').trim())
          .filter(Boolean),
      );
      const simpleBaseId = nextIndex === 0 ? String(formData.get('id') || '').trim() : '';
      if (simpleBaseId) seenVariantIds.add(simpleBaseId);
      const uniqueExtras = extras.filter((extra) => {
        if (!extra.id || seenVariantIds.has(extra.id)) return false;
        seenVariantIds.add(extra.id);
        return true;
      });
      if (!uniqueExtras.length) return;

      if (nextIndex === 0) {
        if (!simpleBaseId) throw new Error(window.variantStrings?.unavailable || cartError());
        const itemSpecificFields = [...formData.entries()].filter(([key]) => (
          key === 'selling_plan'
          || key === 'parent_id'
          || /^properties\[.+]$/.test(key)
        ));

        formData.append('items[0][id]', simpleBaseId);
        formData.append('items[0][quantity]', String(quantityValue(formData.get('quantity'))));
        itemSpecificFields.forEach(([key, value]) => {
          const itemKey = key.startsWith('properties[')
            ? `items[0][properties]${key.slice('properties'.length)}`
            : `items[0][${key}]`;
          formData.append(itemKey, value);
          formData.delete(key);
        });
        nextIndex = 1;
      }

      uniqueExtras.forEach((extra) => {
        formData.append(`items[${nextIndex}][id]`, extra.id);
        formData.append(`items[${nextIndex}][quantity]`, String(extra.quantity));
        nextIndex += 1;
      });
      formData.delete('id');
      formData.delete('quantity');
    }

    requestedItems(formData) {
      const indexed = new Map();
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^items\[(\d+)]\[(id|quantity)]$/);
        if (!match) continue;
        const item = indexed.get(match[1]) || {};
        item[match[2]] = value;
        indexed.set(match[1], item);
      }

      let rawItems;
      if (indexed.size) {
        const entries = [...indexed.entries()].sort(([left], [right]) => Number(left) - Number(right));
        const valid = entries.every(([index, item], position) => {
          const itemQuantity = Number.parseInt(item.quantity, 10);
          return Number(index) === position
            && String(item.id || '').trim()
            && Number.isSafeInteger(itemQuantity)
            && itemQuantity > 0;
        });
        if (!valid) return [];
        rawItems = entries.map(([, item]) => ({
          variantId: String(item.id).trim(),
          quantity: Number.parseInt(item.quantity, 10),
        }));
      } else {
        rawItems = [{ variantId: String(formData.get('id') || '').trim(), quantity: quantityValue(formData.get('quantity')) }];
      }
      const aggregated = new Map();
      rawItems.forEach(({ variantId, quantity }) => {
        if (variantId) aggregated.set(variantId, (aggregated.get(variantId) || 0) + quantity);
      });
      return [...aggregated].map(([variantId, quantity]) => Object.freeze({ variantId, quantity }));
    }

    setBusy(isBusy, submitButton, isLocked = false) {
      this.toggleAttribute('aria-busy', isBusy);
      const currentButtons = [submitButton, ...document.querySelectorAll(`sticky-atc[data-section="${escapeSelector(this.dataset.section || '')}"] .sticky-atc__add_button`)].filter(Boolean);

      if (isBusy) {
        this._busyControls = new Map(currentButtons.map((button) => [button, {
          disabled: Boolean(button.disabled),
          ariaDisabled: button.getAttribute?.('aria-disabled'),
        }]));
      }

      const buttons = isBusy || isLocked ? currentButtons : [...(this._busyControls?.keys() || [])];
      buttons.forEach((button) => {
        if (isBusy) {
          button.disabled = true;
          button.setAttribute?.('aria-disabled', 'true');
        } else if (isLocked) {
          button.disabled = true;
          button.setAttribute?.('aria-disabled', 'true');
        } else {
          const previous = this._busyControls?.get(button);
          button.disabled = previous?.disabled || false;
          if (previous?.ariaDisabled == null) button.removeAttribute?.('aria-disabled');
          else button.setAttribute?.('aria-disabled', previous.ariaDisabled);
        }
        button.classList.toggle('loading', isBusy);
        const spinner = button.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isBusy);
      });
      if (!isBusy) this._busyControls = undefined;
    }

    clearError() {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.hidden = true;
      const sticky = document.querySelector(`sticky-atc[data-section="${escapeSelector(this.dataset.section || '')}"] .sticky-atc__error`);
      if (sticky) {
        sticky.textContent = '';
        sticky.style.display = 'none';
      }
    }

    showError(message) {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      const target = this.querySelector('.product-form__error-message');
      if (target) target.textContent = message;
      if (wrapper) wrapper.hidden = false;
      const sticky = document.querySelector(`sticky-atc[data-section="${escapeSelector(this.dataset.section || '')}"] .sticky-atc__error`);
      if (sticky) {
        sticky.textContent = message;
        sticky.style.display = '';
      }
    }
  }

  class IzzatQuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._onChange = (event) => {
        if (event.target.matches('input[type="radio"][name="quantity"]')) this.sync(event.target);
        if (event.target.matches('.quantity-break__variant-select')) this.syncPerUnitVariant(event.target);
      };
      this.addEventListener('change', this._onChange);
      this.sync(this.querySelector('input[type="radio"][name="quantity"]:checked'));
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this._initialized = false;
    }

    sync(input) {
      if (!input) return;
      const quantity = quantityValue(input.value);
      this.dataset.selectedQuantity = String(quantity);
      const form = input.form || document.getElementById(input.getAttribute('form'));
      if (form) {
        let quantityInput = form.querySelector('input[name="quantity"]:not([type="radio"])');
        if (quantityInput) quantityInput.value = String(quantity);
        form.dataset.izzatQuantity = String(quantity);
      }
      const label = input.id ? this.querySelector(`label[for="${escapeSelector(input.id)}"]`) : null;
      this.dispatchEvent(new CustomEvent('izzat-theme-v2:quantity-change', {
        bubbles: true,
        detail: { sectionId: this.dataset.section || '', quantity, input, label },
      }));
      updateOfferState(this.dataset.section, {
        quantity,
        formattedTotal: label?.querySelector('.quantity-break__price')?.textContent?.trim() || '',
      });
    }

    syncPerUnitVariant(select) {
      const selector = select.closest('.quantity-break__selector-item');
      if (!selector) return;
      let variants = [];
      try {
        variants = JSON.parse(this.querySelector(':scope > script[type="application/json"]')?.textContent || '[]');
      } catch (_error) {
        return;
      }
      const options = [...selector.querySelectorAll('select')].map((item) => item.value);
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));
      selector.dataset.selectedId = variant?.available ? String(variant.id) : '';
    }
  }

  class IzzatQuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.input = this.querySelector('input[type="number"], input[name="quantity"]');
      this._onClick = (event) => {
        const button = event.target.closest('button[name="plus"], button[name="minus"]');
        if (!button || !this.input) return;
        event.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = Number(this.input.min || this.input.dataset.min) || 1;
        const max = Number(this.input.max || this.input.dataset.max) || Number.POSITIVE_INFINITY;
        const direction = button.name === 'plus' ? 1 : -1;
        this.input.value = String(Math.min(max, Math.max(min, quantityValue(this.input.value) + direction * step)));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this._onChange = () => {
        if (!this.input) return;
        this.input.value = String(quantityValue(this.input.value));
        document.dispatchEvent(new CustomEvent('izzat-theme-v2:quantity-change', {
          detail: { sectionId: this.dataset.section || '', quantity: quantityValue(this.input.value), input: this.input },
        }));
      };
      this.addEventListener('click', this._onClick);
      this.input?.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.input?.removeEventListener('change', this._onChange);
      this._initialized = false;
    }
  }

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  define('product-form', IzzatProductForm);
  define('quantity-breaks', IzzatQuantityBreaks);
  define('quantity-input', IzzatQuantityInput);
  window.IzzatThemeV2 = Object.freeze({
    VERSION,
    debounce,
    quantityValue,
    currencySubunitScale,
    majorToSubunits,
    formatMoney,
    sectionProductForm,
    shopifyRoute,
    offerState,
    updateOfferState,
    runSingleFlight,
    runSectionFlight,
    sectionBusy,
    isRecord,
    cartLines,
    cartMatches,
    reconcileCart,
    cartMutationLocked,
    cartMutationBusy,
    cartMutationPending,
    runCartMutation,
    cartCheckoutBlocked,
    guardCartCheckoutClick,
    lockCartMutation,
    cartUnknownMessage,
    createCartUnknownError,
    isCartUnknownError,
    createCartRejectedError,
    isCartRejectedError,
  });
})();

/* Izzat deferred image hydrator (fix 2026-08-15)
   O snippet deferred-image-markup.liquid gera imgs com data-src/data-srcset
   e um gif de 1px no src, mas o codigo que devolvia a URL real nunca foi
   incluido no tema. Este bloco hidrata todas as [data-izzat-deferred-image];
   o loading="lazy" nativo continua adiando o download do que esta fora da tela. */
(function () {
  function hydrate(img) {
    if (!img || img.__izzatHydrated) return;
    img.__izzatHydrated = true;
    var srcset = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.removeAttribute('data-izzat-deferred-image');
  }
  function hydrateAll(root) {
    (root && root.querySelectorAll ? root : document)
      .querySelectorAll('img[data-izzat-deferred-image]')
      .forEach(hydrate);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hydrateAll(); });
  } else {
    hydrateAll();
  }
  /* imgs inseridas depois (clones de slider, quick view, secoes via fetch) */
  if ('MutationObserver' in window) {
    new MutationObserver(function () { hydrateAll(); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }
})();
